import { token } from '@atlaskit/tokens';

import { sessionKeys } from '@confluence/storage-manager/entry-points/SessionStorageKeys.ts';

/** On a light subtree's root: where it is now, `outside view`, `in buffer` or `in view`. */
export const LIGHT_SUBTREE_ATTRIBUTE = 'data-light-subtree';
export const LIGHT_NODE_ATTRIBUTE = 'data-light-node';
/** On the marker before the first block of each batch after the first; its value is the line's label. */
export const LIGHT_BATCH_END_ATTRIBUTE = 'data-light-batch-end';

type Listener = (enabled: boolean) => void;

let enabled: boolean | undefined;
let debugStyle: HTMLStyleElement | undefined;
const listeners = new Set<Listener>();
const nodeMarkers = new Map<string, (element: HTMLElement | null) => void>();

export function isLightModeDebugEnabled(): boolean {
	if (enabled === undefined) {
		try {
			enabled = window.sessionStorage.getItem(sessionKeys.LIGHT_MODE_DEBUG) === 'true';
		} catch {
			// No window during SSR, and browsers can block storage.
			enabled = false;
		}
	}
	return enabled;
}

export function setLightModeDebugEnabled(value: boolean): void {
	enabled = value;
	try {
		if (value) {
			// eslint-disable-next-line check-set-storage-usage/no-unimported-storage-set -- Dev-only setting; browser storage controls block keys that are not registered with them.
			window.sessionStorage.setItem(sessionKeys.LIGHT_MODE_DEBUG, 'true');
		} else {
			window.sessionStorage.removeItem(sessionKeys.LIGHT_MODE_DEBUG);
		}
	} catch {}
	updateLightModeDebugStyle();
	listeners.forEach((listener) => listener(value));
}

export function subscribeToLightModeDebug(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** Adds or removes the stylesheet that draws the debug outlines, labels and batch lines. */
export function updateLightModeDebugStyle(): void {
	if (!isLightModeDebugEnabled()) {
		debugStyle?.remove();
		debugStyle = undefined;
		return;
	}
	if (debugStyle || typeof document === 'undefined') {
		return;
	}
	debugStyle = document.createElement('style');
	debugStyle.textContent = debugCss();
	document.head.appendChild(debugStyle);
}

/** One stable ref per node type, so light components keep the same ref across renders. */
export function getLightNodeMarker(nodeType: string): (element: HTMLElement | null) => void {
	let marker = nodeMarkers.get(nodeType);
	if (!marker) {
		marker = (element) => element?.setAttribute(LIGHT_NODE_ATTRIBUTE, nodeType);
		nodeMarkers.set(nodeType, marker);
	}
	return marker;
}

// Labels are absolutely positioned and outlines take no space, so the page keeps its layout. A batch
// line's label sits above it, in the gap between blocks, so it covers less of the next block.
function debugCss(): string {
	const subtree = `[${LIGHT_SUBTREE_ATTRIBUTE}]`;
	const inBuffer = `[${LIGHT_SUBTREE_ATTRIBUTE}="in buffer"]`;
	const inView = `[${LIGHT_SUBTREE_ATTRIBUTE}="in view"]`;
	const batchEnd = `[${LIGHT_BATCH_END_ATTRIBUTE}]`;
	const label = `position: absolute; z-index: 1; padding: 0 ${token('space.050')}; font: ${token('font.body.small')}; font-weight: ${token('font.weight.semibold')}; color: ${token('color.text.inverse')}; white-space: nowrap; pointer-events: none;`;
	return `
${subtree} { position: relative; outline: 2px solid ${token('color.border.accent.magenta')}; outline-offset: -2px; }
${subtree}::after { content: attr(${LIGHT_SUBTREE_ATTRIBUTE}); top: 0; right: 0; background: ${token('color.background.accent.magenta.bolder')}; ${label} }
${inBuffer} { outline-color: ${token('color.border.accent.yellow')}; }
${inBuffer}::after { background: ${token('color.background.accent.yellow.bolder')}; }
${inView} { outline-color: ${token('color.border.accent.green')}; }
${inView}::after { background: ${token('color.background.accent.green.bolder')}; }
[${LIGHT_NODE_ATTRIBUTE}] { outline: 1px dashed ${token('color.border.accent.blue')}; outline-offset: -1px; }
${batchEnd} { position: relative; height: 0; }
${batchEnd}::before { content: ''; position: absolute; left: 0; right: 0; top: -1px; border-top: 2px dashed ${token('color.border.accent.orange')}; pointer-events: none; }
${batchEnd}::after { content: attr(${LIGHT_BATCH_END_ATTRIBUTE}); bottom: 1px; left: 0; background: ${token('color.background.accent.orange.bolder')}; ${label} }
`;
}
